/**
 * The Atlas's frame: every block one tick runs, as an ordered list in
 * `src/frame.ts`'s shape, and the tick that walks it.
 */
import * as THREE from 'three';

import { runSystems, type FrameProfiler, type FrameSystem } from '../frame.ts';
import { simApi } from '../sim/api.ts';
import { gameClock, SOL_SECONDS } from '../world/clock.ts';
import { flyoverFrame } from './dtmPlot.ts';
import type { AtlasLive, AtlasState } from '../atlas';
import type { WorldVec } from '../world/frame';
import type { AtmosphereViewHandle } from './atmosphereView';
import type { BodyReadersHandle } from './bodyReaders';
import type { BodySceneHandle } from './bodyScene';
import type { CameraRigHandle } from './cameraRig';
import type { CascadesHandle } from './cascades';
import type { DescentAnchorHandle } from './descentAnchor';
import type { DescentLoadHandle } from './descentLoad';
import type { DojoPaintsHandle } from './dojoPaints';
import type { ExposureHandle } from './exposure';
import type { FarFieldHandle } from './farField';
import type { FocusGroundHandle } from './focusGround';
import type { FocusRigHandle } from './focusRig';
import type { FrameGovernorHandle } from './frameGovernor';
import type { GlobeMaterialHandle } from './globeMaterial';
import type { GroundColourHandle } from './groundColour';
import type { HexViewHandle } from './hexView';
import type { InjectsHandle } from './injects';
import type { InputHandle } from './input';
import type { MountHandle } from './mount';
import type { OverlayViewHandle } from './overlayView';
import type { PickHandle } from './pick';
import type { PlotBuildHandle } from './plotBuild';
import type { PlotControlsHandle } from './panels/plotControls';
import type { PlotMountHandle } from './plotMount';
import type { PlotSunHandle } from './plotSun';
import type { TelemetryPanelHandle } from './panels/telemetry';
import type { PyramidViewHandle } from './pyramidView';
import type { RoverDriveHandle } from './roverDrive';
import type { SelectionHandle } from './selection';
import type { ShaderWarmHandle } from './shaderWarm';
import type { SolarScrubHandle } from './solarScrub';
import type { StructureViewHandle } from './structureView';
import type { ViewClockHandle } from './viewClock';
import type { WorldHostState } from './worldHost';
import type { ZoomLadderHandle } from './zoomLadder';
import { TEST_SWITCHES } from '../urlFlags.ts';

/** One Atlas tick: its seconds, whether the pane is on screen, and what one block hands a later one. */
export interface AtlasFrameCtx {
  /** `performance.now()` as the tick began, for `lastTickMs`. */
  tickT0: number;
  /** The pane is laid out 0 wide (a menu closed over it): nothing is drawn or sampled. */
  hidden: boolean;
  /** The frame's own seconds since the last tick, clamped at 0.1. */
  dt: number;
  /** `clock.elapsedTime` once `dt` is drained. */
  t: number;
  /** The exposure is still easing toward its band's target. */
  easing: boolean;
  /** Something on screen moved this frame; the governor samples only these. */
  animating: boolean;
  /** `stage.begin()` handed this frame the renderer. */
  drawing: boolean;
}

const whenVisible = (c: AtlasFrameCtx): boolean => !c.hidden;
const whenDrawing = (c: AtlasFrameCtx): boolean => c.drawing;
const whenNotDrawing = (c: AtlasFrameCtx): boolean => !c.drawing;

/** No block here is profiled, so the runner is never asked to mark one. */
const NO_PROFILER: FrameProfiler = { mark() {}, end() {} };

export interface AtlasFrameDeps {
  readonly root: HTMLElement;
  readonly state: AtlasState;
  readonly renderer: THREE.WebGLRenderer;
  readonly mount: MountHandle;
  readonly injects: InjectsHandle;
  readonly viewClockHandle: ViewClockHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly plotSun: PlotSunHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly focusRig: FocusRigHandle;
  readonly exposureHandle: ExposureHandle;
  readonly detailGroup: THREE.Group;
  readonly detailWorld: WorldVec;
  readonly plotMount: PlotMountHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly CINEMA_DUR: number;
  readonly solarScrub: SolarScrubHandle;
  readonly tileGroup: THREE.Group;
  readonly selection: SelectionHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly focusGround: FocusGroundHandle;
  readonly cascades: CascadesHandle;
  readonly shaderWarm: ShaderWarmHandle;
  readonly globeMaterial: GlobeMaterialHandle;
  readonly groundColour: GroundColourHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly ladder: ZoomLadderHandle;
  closeUp(): boolean;
  readonly hexViewSeam: HexViewHandle;
  readonly plotControls: PlotControlsHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly telemetryPanel: TelemetryPanelHandle;
  readonly cameraRig: CameraRigHandle;
  readonly farField: FarFieldHandle;
  readonly overlayView: OverlayViewHandle;
  readonly pick: PickHandle;
  readonly structureView: StructureViewHandle;
  readonly input: InputHandle;
  readonly frameGovernor: FrameGovernorHandle;
  readonly live: AtlasLive;
  readonly bodyScene: BodySceneHandle;
  readonly worldHostState: WorldHostState;
  /** Built after this call in atlas.ts, so read only inside the tick. */
  readonly dojoPaints: DojoPaintsHandle;
  wasCloseUp: boolean;
  badgeT: number;
}

export interface AtlasFrameHandle {
  /** One frame, run by the stage from the sim's own rAF. */
  tick(): void;
  /** The list `tick` walks, in the order it walks it. */
  readonly systems: readonly FrameSystem<AtlasFrameCtx>[];
}

/** `?warmcompileasync=1`: the warm drain back on three's own `compileAsync`. */
const THREE_COMPILE_ASYNC = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).get('warmcompileasync') === '1';

/**
 * `renderer.compileAsync` that lets go of a material disposed while its link is
 * polled; three's own reads `isReady` off the program that took with it, in a timer.
 */
function compileReleasable(renderer: THREE.WebGLRenderer, obj: THREE.Object3D,
  camera: THREE.Camera, scene: THREE.Scene): Promise<void> {
  const pending = renderer.compile(obj, camera, scene);
  return new Promise<void>((resolve) => {
    const check = (): void => {
      for (const m of pending) {
        const program = renderer.properties.has(m)
          ? (renderer.properties.get(m) as { currentProgram?: { isReady(): boolean } }).currentProgram
          : undefined;
        if (program === undefined || program.isReady()) pending.delete(m);
      }
      if (pending.size === 0) resolve();
      else setTimeout(check, 10);
    };
    if (renderer.extensions.has('KHR_parallel_shader_compile')) check();
    else setTimeout(check, 10);
  });
}

export function createAtlasFrame(deps: AtlasFrameDeps): AtlasFrameHandle {
  const {
    root, state, renderer, atmosphereView, detailGroup, detailWorld, CINEMA_DUR, solarScrub, tileGroup, pyramidView,
    cascades, shaderWarm, globeMaterial, ladder, closeUp, hexViewSeam, telemetryPanel, cameraRig, farField, pick,
    input, frameGovernor,
  } = deps;
  const { stage, scene, subtree, camera, cameraWorld, worldFrame } = deps.mount;
  const { CLOCK_TWICE, DAY_BADGE_OLD, DAY_BADGE_EVERY_FRAME, PANEL_KEEPS_PREVIEW, ROVER_WARP_DT, cameraOnDatum } = deps.injects.state;
  const { viewT, viewClock, beginPreview } = deps.viewClockHandle;
  const { syncAtmoMount, updateAtmoFrame } = atmosphereView;
  const { seasonLight, setSunScale, applySun } = deps.plotSun;
  const { bodyClimate } = deps.bodyReaders;
  const { focusCam, shipFocused, band } = deps.focusRig;
  const { updateExposure } = deps.exposureHandle;
  const { plotUpWorld } = deps.plotMount;
  const { warmSpend, ringSpend, surveyRings, ensurePyramidPlot } = deps.descentLoad;
  const { solar } = solarScrub;
  const { selectMark, hoverMark, applySelection } = deps.selection;
  const { updateFocusGround } = deps.focusGround;
  const { warmDescentPrograms } = shaderWarm;
  const { followPlotGroundColour } = deps.groundColour;
  const { ensurePlotMesh } = deps.plotBuild;
  const { syncDescentAnchor } = deps.descentAnchorHandle;
  const { hexOverlay, syncHexOverlay } = hexViewSeam;
  const { syncPlotLabels, pcSol, pcLs, setPlaying, endSolScrub, endForecastScrub } = deps.plotControls;
  const { JUMP_EASE_S, jumpEaseFromLook, roverFocus } = deps.roverDrive;
  const { renderTelemetry } = telemetryPanel;
  const {
    cinemaPos, cinemaLook, placeFocusCamera, readCameraWorld, stopCinema, orbitViewBy, surfaceRegime, orbit,
    WALK_BODY_R_M, camPos, camTarget, _aimW, applyNearClipInject, plotCamera, plotDistanceFromAltitude, targetPos,
    plotEye, targetLook, plotEyeFloorM, applyPlotTilt,
  } = cameraRig;
  const {
    syncPlotFrame, plotFrameToWorld, placePlotNode, farFieldWanted, syncGroundSky, placeFarField, drawFrame,
  } = farField;
  const { renderOverlay, syncBadge, updateCorridor } = deps.overlayView;
  const { governor, pixelRatio } = frameGovernor;
  const clock = new THREE.Clock();
  const _focus = new THREE.Vector3();
  // --inject scale-stale: pins the drawn half to base, so a governor step
  // that never reached the renderer is the thing under test.
  const SCALE_STALE = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('scalestale') === '1';
  let pulseAccum = 0;
  const PULSE_HZ = 15;
  const ctx: AtlasFrameCtx = {
    tickT0: 0, hidden: false, dt: 0, t: 0, easing: false, animating: false, drawing: false,
  };

  let elClockHold: HTMLElement | null = null;
  let clockHoldPainted: string | null = null;

  const systems: FrameSystem<AtlasFrameCtx>[] = [

  { name: 'warp-row', when: whenVisible, update: () => {
    // The one clock eases onto a picked level over seconds and can be dropped
    // under the player by the sim, so this row reads it every frame rather
    // than on a state change. The paint is memoized on what it draws.
    hexViewSeam.warpRow?.paint();
  } },

  { name: 'clock-hold', when: whenVisible, update: () => {
    // The sim decides when the map holds the clock; this only says why, and
    // writes the DOM only when the line changes.
    const line = simApi.clockHold();
    if (line === clockHoldPainted) return;
    elClockHold ??= root.querySelector<HTMLElement>('#atlas-clock-hold');
    if (!elClockHold) return;
    clockHoldPainted = line;
    elClockHold.classList.toggle('hidden', line === null);
    elClockHold.textContent = line ?? '';
  } },

  { name: 'view-layers', when: whenVisible, update: () => {
    // The TRAJECTORY row's own state lives behind simApi, not a layerPref, so
    // the pilot panel's button flipping it while this view is open only
    // reaches the row through a repaint here - memoized, like the row above.
    if (!hexViewSeam.layerTickPaintOff) hexViewSeam.viewLayers?.paint();
  } },

  { name: 'clock', update: (c) => {
    // The stage is not ticked while the menu is closed, so an unclamped delta
    // would hand the easing the whole closed interval on reopen.
    c.dt = Math.min(clock.getDelta(), 0.1);
    c.t = clock.elapsedTime;
  } },

  { name: 'inertia', update: (c) => {
    const dt = c.dt;
    // Inertia decay — apply leftover orbit velocity, decays fast (half-life
    // ~100ms) so the player can click within a beat of releasing the drag.
    if (!input.isDragging) {
      if (Math.abs(input.yawInertia) > 0.002) {
        focusCam.orbitBy(-input.yawInertia * dt, 0);
        input.yawInertia *= Math.exp(-dt * input.INERTIA_DECAY);
        deps.live.cameraDirty = true;
      } else {
        input.yawInertia = 0;
      }
      if (Math.abs(input.pitchInertia) > 0.002) {
        orbitViewBy(0, input.pitchInertia * dt);
        input.pitchInertia *= Math.exp(-dt * input.INERTIA_DECAY);
        deps.live.cameraDirty = true;
      } else {
        input.pitchInertia = 0;
      }
    }
  } },

  { name: 'handover', update: (c) => {
    const dt = c.dt;
    // ── the handover ──────────────────────────────────────────────────────
    // `descent` used to ease the claim's SCALE from 0.02 to 1 and its position
    // from a point on the globe to the origin, which is a fake fall built to
    // hide a cut. The plot now sits at its own scale from the moment it is
    // drawn; what is left of the blend is the map annotations and the orrery,
    // which fade because nothing else in frame depends on where they land.
    // The plot mesh does NOT fade with it below: the camera below switches to
    // and from the plot's own rig the instant `surfaceRegime()` flips, and a
    // lagging `descent` (~2 s to clear its own 0.004 floor) left it drawn
    // against the wrong eye on the way back up - floating rocks over an orange
    // disc, since the eye had already jumped to the scene origin.
    //
    // PLOT and SURFACE are one tier label, so the regime edge is an altitude
    // and not something `syncTierToAltitude` can see.
    ladder.cross();
    // A CONDITION rather than the edge alone: if the claim's field lands after
    // the crossing, the edge-only ask above used to hit ensurePlotMesh's own
    // `!field` guard and never fire again, so the walkaround never mounted.
    // The guard at its top (plot || plotMeshLoading || !field || !claimUrl)
    // makes every repeat here free once the build is up or already running.
    // Where there is no claim to ask for, the plot is built from the leaves the
    // ring has under the landing point. Asked from here rather than from the
    // rung that asks for a claim, because it waits on the tiles being resident
    // and not on the altitude; its own guards make the frames it has nothing to
    // do on two comparisons.
    if (state.tier >= (deps.descentLoad.state.claimTierLate ? 4 : 3)) void ensurePyramidPlot();
    if (!deps.injects.state.plotEdgeOnly && ladder.regime !== 'aloft' && !deps.live.plot && !deps.live.plotMeshLoading && !deps.descentLoad.state.plotError && deps.live.plotField) void ensurePlotMesh();
    // The blend follows the HANDOVER, not the band edge: it retires the orrery
    // and with it the pyramid, and on a body with no mesh to replace them there
    // is nothing else drawn. A staged build sets `plot` on the frame it reveals
    // the mesh and enters the rig, so this fade starts there too and the ground
    // a person sees is the pyramid's until there is a mesh to take its place.
    ladder.ease(dt);
  } },

  { name: 'pivot', update: (c) => {
    const dt = c.dt;
    // Selecting a cell pulls the orbit centre onto it, so zooming afterwards
    // closes on the plot you were looking at rather than on the middle of the
    // claim. Eased, because snapping the pivot mid-drag is disorienting.
    if (surfaceRegime() && deps.live.selectedSub && deps.live.plot && deps.live.plotField) {
      const yLocal =
        (deps.live.plotField.heightAt(deps.live.selectedSub.x, deps.live.selectedSub.z) -
          deps.live.plotField.meta.elevation.min_m) * solar.exag;
      _focus.set(deps.live.selectedSub.x, yLocal, deps.live.selectedSub.z);
      deps.live.plot.group.updateMatrix();
      _focus.applyMatrix4(deps.live.plot.group.matrix);
      if (orbit.lookAt.distanceToSquared(_focus) > 1e-8) {
        orbit.lookAt.lerp(_focus, 1 - Math.exp(-dt * 3.0));
        deps.live.needsRender = true;
      }
    } else if (surfaceRegime() && deps.live.plot && !deps.roverDrive.state.driving) {
      // With nothing selected the pivot is floored every frame, not once at
      // entry: the mesh can land after enterPlotRegime has already aimed, and
      // WASD carries the centre onto terrain of its own height. `plotEye`
      // measures the eye FROM the pivot, so a pivot under the ground takes the
      // whole descent with it. Up only, which is what leaves R/F usable: F
      // pushes the aim down until the floor holds it.
      if (deps.live.plot.liftAboveGround(orbit.lookAt, 0)) deps.live.needsRender = true;
      // And out of the boulders, for the same reason and in the same place: the
      // pivot is the only part of the walkaround rig a rock can reach. The eye
      // rides SURFACE_EYE_FLOOR_M over the drawn mesh and the tallest rock the
      // field draws at Jezero stands 0.63 m, so stopping the pivot is what
      // stops the view - `plotEye` measures the eye FROM it, and the eye's own
      // lerp is what makes the stop read as smooth rather than as a wall.
      if (deps.live.plot.pushOutOfRocks(orbit.lookAt, WALK_BODY_R_M)) {
        deps.live.plot.liftAboveGround(orbit.lookAt, 0);
        deps.live.needsRender = true;
      }
    }
  } },

  { name: 'sun', update: () => {
    // The sun is the ephemeris's at every tier, not only where the plot is:
    // the scattering tables read the dust column off `lsDeg` and the sky reads
    // its irradiance off it, so leaving both on the pre-registry stand-in until
    // the first tier-5 frame would step the sky on the way into the claim.
    solarScrub.syncSolar();
  } },

  { name: 'clock-travel', update: (c) => {
    const dt = c.dt;
    // ── the clock's own travel ─────────────────────────────────────────────
    // src/main.ts advances the one clock before this tick, at the warp
    // control's rate; the plot follows it here unless a scrub or a sweep is
    // previewing another date, which the clock runs on under.
    if (deps.viewClockHandle.state.clockRunning && !deps.viewClockHandle.state.playing && !deps.viewClockHandle.state.forecasting && deps.viewClockHandle.state.previewT === null && state.tier >= 5) {
      if (CLOCK_TWICE) {
        gameClock().update(dt);
        solarScrub.syncSolar();
      }
      applySun();
      hexViewSeam.sunrise?.sample();
      syncPlotLabels();
      pcSol.value = String(Math.round(solar.solPhase * 1000));
      pcLs.value = String(Math.round(solar.lsDeg));
      // Re-survey on a 4-degree throttle, same as the year sweep.
      deps.live.needsRender = true;
    }
  } },

  { name: 'plot-update', update: (c) => {
    const dt = c.dt;
    // ── plot animation ────────────────────────────────────────────────────
    // A pane height of 0 is `--edge --inject speckle`: with no pane to measure a
    // rock against there is no draw floor, and every band draws whatever it
    // covers on screen.
    deps.live.plot?.update(dt, camPos, camera, farField.edgeRockFloorOff ? 0 : Math.max(1, root.clientHeight));
  } },

  { name: 'marks', update: (c) => {
    const dt = c.dt;
    // The leaves are the ground above the walkaround's edge, so that is where
    // the marks draw; below it the plot mesh's own shader carries both.
    const marksOn = !surfaceRegime() && tileGroup.visible && state.tier >= 5;
    selectMark.setVisible(marksOn);
    hoverMark.setVisible(marksOn);
    if (marksOn && (deps.selection.state.markHeightVersion !== pyramidView.tileHeightVersion || deps.selection.state.markExag !== solar.exag)) {
      deps.selection.state.markHeightVersion = pyramidView.tileHeightVersion;
      deps.selection.state.markExag = solar.exag;
      selectMark.refresh();
      hoverMark.refresh();
      deps.live.needsRender = true;
    }
    selectMark.update(dt);
    hoverMark.update(dt);
    if (selectMark.drawn || hoverMark.drawn) deps.live.needsRender = true;
  } },

  { name: 'sweep-stop', update: () => {
    // The sweep buttons hide above the plot's tiers, so a sweep left running
    // there would hold the whole Atlas on its previewed date with no way to stop it.
    if (deps.viewClockHandle.state.playing && state.tier < 5) setPlaying(null);
  } },

  { name: 'preview-end', update: () => {
    // A slider's preview ends with the slider: leaving the plot's tiers, losing
    // the plot or minimizing the panel hides it, and not every browser blurs an
    // input it hides.
    if (!PANEL_KEEPS_PREVIEW) {
      if (deps.viewClockHandle.state.solScrubbing && !pcSol.checkVisibility()) endSolScrub();
      if (deps.viewClockHandle.state.forecasting && !pcLs.checkVisibility()) endForecastScrub();
    }
  } },

  { name: 'sweep', update: (c) => {
    const dt = c.dt;
    if (deps.live.plotField) {
      if (deps.viewClockHandle.state.playing === 'sol') {
        // Sweeping the sol only moves the shadow. The insolation integral is
        // taken over a WHOLE day, so no verdict can change inside one — which
        // is why this is free and can run every frame. The sweep is a clock
        // RATE, not a local counter: 0.055 sols per second is what it always
        // ran at, expressed as the simulated seconds that buys.
        beginPreview();
        viewClock.scrub(dt * 0.055 * SOL_SECONDS);
        solarScrub.syncSolar();
        applySun();
        syncPlotLabels();
        pcSol.value = String(Math.round(solar.solPhase * 1000));
      } else if (deps.viewClockHandle.state.playing === 'year') {
        // Sweeping the year DOES move verdicts, and re-scoring is a horizon
        // march per cell per sun position. Re-survey on a 4-degree throttle and
        // let the shadow interpolate between.
        const before = solar.lsDeg;
        beginPreview();
        viewClock.scrub((dt * 9 / 360) * solarScrub.bodyYearS());
        solarScrub.syncSolar();
        if (Math.floor(solar.lsDeg / 4) !== Math.floor(before / 4)) {
          // The mesh re-surveys because its overlay texture is painted from the
          // same pass; without one the field's own scoring is the whole job.
          const climate = bodyClimate();
          if (deps.live.plot) deps.live.plotSubs = deps.live.plot.measure(surveyRings(deps.live.plotField), solar.lsDeg);
          else if (climate) deps.live.plotField.scoreSubPlots(deps.live.plotSubs, solar.lsDeg, climate);
          if (deps.live.selectedSub) {
            deps.live.selectedSub = deps.live.plotSubs.find((p) => p.id === deps.live.selectedSub!.id) ?? deps.live.selectedSub;
            // Dust season costs the array real area — the footprint grows as
            // the sun goes weak, which is the whole point of drawing it.
            applySelection();
          }
          renderOverlay();
        }
        applySun();
        syncPlotLabels();
        pcLs.value = String(Math.round(solar.lsDeg));
      }
    }
  } },

  { name: 'day-badge', update: () => {
    // The sun crosses the horizon between overlay redraws, so the badge is
    // re-read on every frame the clock moved; it writes only when its text does.
    if (DAY_BADGE_EVERY_FRAME || (!DAY_BADGE_OLD && viewT() !== deps.badgeT)) {
      deps.badgeT = viewT();
      syncBadge();
    }
  } },

  { name: 'cinema', update: (c) => {
    const dt = c.dt;
    // ── camera ────────────────────────────────────────────────────────────
    if (deps.cameraRig.cinema !== null && deps.live.plot && state.tier >= 5) {
      deps.cameraRig.cinema += dt;
      if (deps.cameraRig.cinema >= CINEMA_DUR) stopCinema();
      else {
        // The flyover's own path is in the plot's frame, the one the rig is
        // in, so it goes through the same handoff the walkaround's pose does.
        flyoverFrame(deps.cameraRig.cinema / CINEMA_DUR, deps.live.plot, cinemaPos, cinemaLook);
        camPos.copy(cinemaPos);
        camTarget.copy(cinemaLook);
        syncPlotFrame();
        camera.position.set(0, 0, 0);
        camera.up.copy(plotUpWorld);
        camera.lookAt(plotFrameToWorld(_aimW.copy(camTarget)));
        placePlotNode();
        // The flyover returns before the tick's own camera branch, so without
        // this the inject override would not reach a frame drawn mid-flyover.
        applyNearClipInject();
      }
    }
  } },

  { name: 'drive', update: (c) => {
    const dt = c.dt;
    if (deps.roverDrive.state.driving && deps.live.plot?.rover) {
      const r = deps.live.plot.rover;
      const manual = input.held.has('w') || input.held.has('s') || input.held.has('a') || input.held.has('d');
      // Survey jobs take priority over auto, but manual always wins.
      const surveyCtl = !manual ? r.surveyDrive(dt) : null;
      // A hand on the keys always wins — auto is a mode, not a lockout.
      const ctl = surveyCtl ?? (telemetryPanel.auto && !manual ? r.autoDrive(dt) : {
        throttle: (input.held.has('w') ? 1 : 0) - (input.held.has('s') ? 1 : 0),
        steer: (input.held.has('d') ? 1 : 0) - (input.held.has('a') ? 1 : 0),
        brake: input.held.has('shift'),
      });
      // The Atlas's own real seconds: a warp moves the sun, never the rover.
      r.update(dt, ctl);
      // `atlas-clock-check --leg surface --inject rover-warp-dt`: up to 2 s more a frame on the warped seconds.
      if (ROVER_WARP_DT) for (let s = 0; s < Math.min(2, dt * (gameClock().rate - 1)); s += 0.05) r.update(0.05, ctl);
      // Check for cell crossings — both manual and auto driving survey.
      r.checkSurvey();
      telemetryPanel.telemAccum += dt;
      if (telemetryPanel.telemAccum > 0.25) { telemetryPanel.telemAccum = 0; renderTelemetry(); }
      // The camera watches the rover rather than a fixed point, but only while
      // FOLLOWING it: driving keeps the rover moving under WASD whether or not
      // the camera is watching, which is what lets re-entry land on the anchor
      // with the rover still deployed and driveable. Orbit angles stay under
      // the mouse, so you can still swing round it and zoom while following.
      // Region metres -> the plot's own frame through the group's own
      // transform, rather than re-deriving the centroid out here where it could
      // drift. The rig is in that frame, not the drawn one.
      r.focus(roverFocus);
      deps.live.plot.group.updateMatrix();
      roverFocus.applyMatrix4(deps.live.plot.group.matrix);
      if (deps.injects.state.legacyRoverSnap || (deps.roverDrive.state.jumpEaseT === null && deps.roverDrive.state.followRover)) {
        orbit.lookAt.copy(roverFocus);
      } else if (deps.roverDrive.state.jumpEaseT !== null) {
        // JUMP TO ROVER: ease eye and look-at onto the deploy framing over
        // JUMP_EASE_S rather than copying it in the one frame that whiplashed.
        deps.roverDrive.state.jumpEaseT += dt;
        const t = Math.min(1, deps.roverDrive.state.jumpEaseT / JUMP_EASE_S);
        const k = t * t * (3 - 2 * t);
        orbit.lookAt.lerpVectors(jumpEaseFromLook, roverFocus, k);
        focusCam.setOrientation(
          THREE.MathUtils.lerp(deps.roverDrive.state.jumpEaseFromYaw, -r.state.yaw, k),
          THREE.MathUtils.lerp(deps.roverDrive.state.jumpEaseFromPitch, 0.30, k),
        );
        cameraRig.plotTilt = deps.roverDrive.state.jumpEaseFromTilt * (1 - k);
        focusCam.setAltitude(THREE.MathUtils.lerp(deps.roverDrive.state.jumpEaseFromAlt, 12, k));
        if (t >= 1) deps.roverDrive.state.jumpEaseT = null;
        deps.live.cameraDirty = true;
      }
      deps.live.needsRender = true;
    } else {
      input.applyMovement(dt);
    }
  } },

  { name: 'close-up', update: () => {
    // Map annotations key off how close the camera is, so they have to be
    // re-applied when that crosses over — otherwise the solar array vanishes on
    // the way down and never comes back on the way up.
    if (state.tier >= 5 && closeUp() !== deps.wasCloseUp) {
      deps.wasCloseUp = closeUp();
      applySelection();
      applySun();
    }
  } },

  { name: 'bodies', update: () => {
    // ── where the camera is ───────────────────────────────────────────────
    //
    // Above the claim the camera IS the scene origin and the world frame brings
    // everything to it; inside the claim the plot's local frame owns it and the
    // altitude is what the two regimes share.
    //
    // The bodies go to the clock's instant BEFORE the camera is solved: the
    // focus point is a body-fixed lat/lon, so a camera placed against last
    // frame's position and rotation looks 24 km off the ground drawn this one.
    deps.bodyScene.state.orrery?.propagate();
  } },

  { name: 'camera', update: (c) => {
    const dt = c.dt;
    cameraRig.syncYawBasis();
    if (deps.cameraRig.cinema !== null || (!plotCamera() && !cameraRig.tileWalkaround())) cameraRig.plotTilt = 0;
    if (deps.cameraRig.cinema === null) {
      if (plotCamera()) {
        // Before the rig, because the aim below is taken through it.
        syncPlotFrame();
        // The plot's own frame is y-up; the focus camera leaves `up` pointing
        // along the body's local vertical, which would tilt the whole claim.
        // `plotUpWorld` is that same y carried out to the drawn frame, which is
        // where `camera.up` is read.
        camera.up.copy(plotUpWorld);
        orbit.distance = plotDistanceFromAltitude();
        // A profile that poses its own eye (the building viewer's, which stands
        // a person's 1.7 m eye under this rig's 5 m floor) is taken as given,
        // floored only clear of the ground itself.
        const viewerEye = deps.live.plot ? deps.structureView.viewerEye?.() ?? null : null;
        if (viewerEye) {
          cameraRig.plotTilt = 0;
          targetPos.copy(viewerEye.eye);
          targetLook.copy(viewerEye.look);
          camPos.copy(targetPos);
          camTarget.copy(targetLook);
          deps.live.plot!.liftAboveGround(camPos, 0.3);
        } else {
          targetPos.copy(plotEye());
          targetLook.copy(orbit.lookAt);
          const lerp = 1 - Math.exp(-dt * 4.5);
          camPos.lerp(targetPos, lerp);
          camTarget.lerp(targetLook, lerp);
        }
        // Floor the camera on the terrain. Below the surface there is nothing to
        // see - the ground is single-sided, so from underneath the world is a
        // hole with a sky in it - and the orbit rig will happily swing you there
        // when the look-at point is near a slope.
        if (deps.live.plot && ladder.descent > 0.5 && !viewerEye) {
          if (cameraOnDatum) deps.live.plot.liftAboveDatum(camPos, plotEyeFloorM());
          else deps.live.plot.liftAboveGround(camPos, plotEyeFloorM());
        }
        // The eye goes to the scene origin and the plot comes to it, which is
        // what the registration buys: `applyCameraRelative` writes every
        // registered node at world minus camera, and until now the drawn camera
        // sat at `camPos` inside the claim instead, so the whole registered
        // world was displaced by the eye's own offset from the plot's centroid.
        // The rig itself stays in the plot's own frame; only the aim is carried
        // out to the drawn one.
        camera.position.set(0, 0, 0);
        camera.lookAt(plotFrameToWorld(_aimW.copy(camTarget)));
        applyPlotTilt();
        placePlotNode();
        applyNearClipInject();
      } else {
        // Before the placement, because it is what the placement's basis is.
        syncDescentAnchor();
        placeFocusCamera();
      }
    }
  } },

  { name: 'camera-world', update: () => {
    // The camera's own heliocentric position, in scene metres: the one number
    // the camera-relative frame subtracts from everything else.
    readCameraWorld();
  } },

  { name: 'ring-at-surface', update: () => {
    // The ring's head start, taken at the one instant it means anything: the
    // frame the band the walkaround's ground goes up in is reached. Here and
    // not in `applyState`, because `band()` is the focus camera's own altitude
    // and a continuous descent crosses it between state changes.
    if (deps.descentLoad.state.ringAtSurface === null && surfaceRegime()) {
      deps.descentLoad.state.ringAtSurface = { warmTiles: warmSpend.tiles, tiles: ringSpend.tiles };
    }
  } },

  { name: 'orrery', update: () => {
    if (deps.bodyScene.state.orrery) {
      const bandNow = band();
      deps.bodyScene.state.orrery.setNamesVisible(bandNow === 'SYSTEM' || bandNow === 'ORBIT');
      // Before the light rig and the disc, which both take it this frame.
      deps.plotSun.updateCover(cameraWorld);
      const cover = deps.plotSun.cover;
      if (bandNow === 'SYSTEM') setSunScale(1);
      else setSunScale(seasonLight() * cover.ground, cover.ground, cover.groundWeight);
      deps.bodyScene.state.orrery.update(cameraWorld);
      // The two bands a body is still drawn as a place rather than as ground,
      // which is where a marker saying where the ship is means anything. Before
      // the frame writes camera-relative positions, so the reticle is at this
      // frame's own heliocentric point and not the last one's.
      deps.bodyScene.state.shipMarker?.setBandVisible(bandNow === 'SYSTEM' || bandNow === 'ORBIT');
      deps.bodyScene.state.shipMarker?.setSelected(shipFocused());
      deps.bodyScene.state.shipMarker?.update(cameraWorld);
      deps.bodyScene.state.belts?.setBandVisible(bandNow === 'SYSTEM' || bandNow === 'ORBIT');
      deps.bodyScene.state.belts?.update();
      deps.bodyScene.state.orrery.layoutLabels(camera, deps.dojoPaints.labelViewportH());
      // After the orrery, which is what writes this body's rotation and its
      // angular-floor scale this frame; both are what the eye is measured in.
      updateCorridor();
      // The walkaround's far field keeps the sun's disc (`placeFarField`).
      if (!ladder.orreryOn && !farFieldWanted()) deps.bodyScene.state.orrery.sol.mesh.visible = false;
      syncGroundSky();
      if (deps.bodyScene.state.bodyNode) {
        detailWorld.x = deps.bodyScene.state.bodyNode.world.x;
        detailWorld.y = deps.bodyScene.state.bodyNode.world.y;
        detailWorld.z = deps.bodyScene.state.bodyNode.world.z;
        detailGroup.quaternion.copy(deps.bodyScene.state.bodyNode.quaternion);
      }
    }
  } },

  { name: 'tiles', update: (c) => {
    const dt = c.dt;
    // After the orrery, because the body's centre and rotation this frame are
    // what a body-fixed camera position is measured against.
    pyramidView.updateTiles(dt);
    followPlotGroundColour(dt);
  } },

  { name: 'focus-ground', update: (c) => {
    const dt = c.dt;
    // After the tiles, because the leaf the focus point reads is one the
    // traversal just decided to draw.
    updateFocusGround(dt);
    pyramidView.updateGroundGlide(dt);
  } },

  { name: 'eye-over-ground', update: () => {
    // And the eye after both: `centreGroundLiftM` holds a centre-anchored eye
    // over the drawn ground, and until here it was holding it over the ground
    // of the tick before this one.
    // A ride placed only by the next frame drew two frames' ride at once whenever a tile change placed it here.
    if ((pyramidView.tileDrawChanged || deps.focusGround.eyeRode) && deps.cameraRig.cinema === null && !plotCamera()) {
      placeFocusCamera();
      readCameraWorld();
      deps.live.needsRender = true;
    }
  } },

  { name: 'far-field', update: () => {
    // After both, because it reads the body's centre and rotation this frame,
    // the plot group's own matrix, and the eye the two above may have moved.
    placeFarField();
  } },

  { name: 'atmosphere', update: () => {
    // Which air the frame needs moves with the REGIME and not only with the
    // focus: a primary's air drawn behind a moon comes down at the edge of that
    // moon's own walkaround frame. A no-op on every frame that does not change
    // it, and the mount itself is amortised from here like the passes below.
    syncAtmoMount();
    // One scattering pass a frame at most, and never on a frame that built a
    // tile mesh - `atmo.update` takes the yield as its last argument.
    updateAtmoFrame(pyramidView.tileMountedThisFrame === 0);
  } },

  { name: 'shadows', update: () => {
    // After the sun frame: the cascades aim along `plotSunDirLocal`, which
    // `applySun` may have just moved this same tick.
    cascades.updateShadowCascades();
  } },

  { name: 'exposure', update: (c) => {
    c.easing = updateExposure();
  } },

  { name: 'overlay', update: () => {
    if (deps.overlayView.state.overlayDirty) {
      deps.overlayView.state.overlayDirty = false;
      renderOverlay();
    }
  } },

  { name: 'hover', update: () => {
    // The grid follows the camera: the resolution is a function of altitude and
    // the cell set of where the camera is looking, so it is asked every frame
    // and rebuilds only when one of those actually moved.
    // Before the grid's own sync, so the cell the cursor is on and the cell the
    // wash is drawn for are read in the same frame.
    pick.updateHover();
  } },

  { name: 'structure-view', when: whenVisible, update: (c) => {
    // After the hover pick, so a building's label falls back on the cell the
    // cursor answered this same frame.
    deps.structureView.update(c.dt);
  } },

  { name: 'hex', update: () => {
    if (hexOverlay.object.visible) syncHexOverlay();
  } },

  { name: 'claim-wash', update: () => {
    // Not gated on the overlay being drawn: the wash is the claim's reading
    // from ORBIT, where the boundary lines are a rung the eye cannot resolve.
    globeMaterial.syncGlobeClaims();
    // The tiles' own textures, same trigger: a claim staked under drawn ground
    // has to reach it without waiting for a level change to mount a new leaf.
    globeMaterial.syncTileClaims();
  } },

  { name: 'redraw', update: (c) => {
    const dt = c.dt;
    // ── should this frame be drawn at all? ────────────────────────────────
    const camMoving = deps.live.cameraDirty
      || camPos.distanceToSquared(targetPos) > 1e-8
      || camTarget.distanceToSquared(targetLook) > 1e-8;
    deps.live.cameraDirty = false;
    const blending = ladder.blending;
    const animating =
      camMoving || blending || c.easing || deps.viewClockHandle.state.playing !== null || deps.cameraRig.cinema !== null ||
      deps.descentLoad.state.plotLoading || input.isDragging || Math.abs(input.yawInertia) > 0.002 ||
      Math.abs(input.pitchInertia) > 0.002 || input.moveInput() ||
      (deps.viewClockHandle.state.clockRunning && state.tier >= 5);
    c.animating = animating;
    if (animating) deps.live.needsRender = true;
    else if (deps.live.selectedSub && ladder.descent > 0.5) {
      pulseAccum += dt;
      if (pulseAccum >= 1 / PULSE_HZ) {
        pulseAccum = 0;
        deps.live.needsRender = true;
      }
    }
  } },

  { name: 'keep-drawing', when: whenVisible, update: () => {
    // Drawn every frame the pane is on screen, `needsRender` or not.
    //
    // Render on demand made an idle Atlas free, and with the Atlas drawing
    // from the sim's own root into the sim's own canvas that stopped being a
    // free choice: the frame the player is looking at has to be a frame
    // somebody drew this tick, which is what `descent-dojo --ship`'s advance
    // bar reads, and the mount is where an idle Atlas used to leave gaps of
    // most of a second. `needsRender` still carries whether the IMAGE
    // changed - the atmosphere's amortised passes and the pulse rate below
    // both set it - so it stays, and the governor still samples animating
    // frames only.
    deps.live.needsRender = true;
  } },

  { name: 'stage-begin', update: (c) => {
    c.drawing = deps.live.needsRender && !c.hidden && stage.begin();
  } },

  { name: 'draw', when: whenDrawing, update: () => {
    deps.live.needsRender = false;
    governor.wantPixelRatio = pixelRatio();
    governor.drawnPixelRatio = SCALE_STALE ? 1 : renderer.getPixelRatio();
    subtree.activate();
    worldFrame.applyCameraRelative(cameraWorld);
    // The plot is registered like everything else and the drawn camera is at
    // the origin in every band, so this is exact inside the claim too.
    // Programs asked for from inside the draw bracket, never outside it.
    // `stage.begin()` is what puts the renderer in the state the Atlas draws
    // in - its own shadow map and exposure - and those are inputs to the
    // program cache key, so a compile from between frames links a program the
    // draw then throws away and links again.
    if (!shaderWarm.linkInLeg && (shaderWarm.warmDue || shaderWarm.warmQuiet < 2)) {
      shaderWarm.warmDue = false;
      warmDescentPrograms();
      if (shaderWarm.warmQuiet < 2) deps.live.needsRender = true;
    }
    if (deps.plotBuild.state.warmQueue.length > 0) {
      const w = deps.plotBuild.state.warmQueue.shift()!;
      const linked = THREE_COMPILE_ASYNC
        ? renderer.compileAsync(w.obj, camera, scene)
        : compileReleasable(renderer, w.obj, camera, scene);
      void linked.then(w.done, w.done);
      if (deps.plotBuild.state.warmQueue.length > 0) deps.live.needsRender = true;
    }
    const renderT0 = performance.now();
    const progs0 = renderer.info.programs?.length ?? 0;
    const geos0 = renderer.info.memory.geometries;
    const texs0 = renderer.info.memory.textures;
    drawFrame();
    pyramidView.lastRenderMs = performance.now() - renderT0;
    if (shaderWarm.warmDrawing) {
      shaderWarm.warmDrawing = false;
      if (shaderWarm.warmTiles) shaderWarm.warmTiles.group.visible = false;
      if (shaderWarm.warmPlot) shaderWarm.warmPlot.group.visible = false;
      if (shaderWarm.warmRocks) shaderWarm.warmRocks.group.visible = false;
      if (shaderWarm.warmStructures) shaderWarm.warmStructures.group.visible = false;
    }
    pyramidView.tileWork.progsIn += (renderer.info.programs?.length ?? 0) - progs0;
    pyramidView.tileWork.geosIn += renderer.info.memory.geometries - geos0;
    pyramidView.tileWork.texsIn += renderer.info.memory.textures - texs0;
  } },

  { name: 'governor', when: whenDrawing, update: (c) => {
    frameGovernor.sample(c.dt, c.animating);
  } },

  { name: 'stage-end', when: whenDrawing, update: () => {
    // Viewport, scissor and render target go back before the sim's frame.
    stage.end();
  } },

  { name: 'no-draw', when: whenNotDrawing, update: () => {
    pyramidView.lastRenderMs = 0;
  } },

  { name: 'atmo-pending', update: () => {
    // One scattering pass a frame is only amortised if there IS a next frame,
    // and this renderer draws on demand. After the render, because the block
    // above consumes `needsRender` before it draws: asking for the frame from
    // inside the update would satisfy the request with the frame that already
    // holds the stale table, and the Atlas would settle showing the sky the sun
    // was at two moves ago. `descent-dojo --sky` reads that as an instrument
    // fault, per sample, off the same `pending` counter.
    if (atmosphereView.atmo && atmosphereView.atmo.pending() > 0) deps.live.needsRender = true;
  } },

  { name: 'tick-time', update: (c) => {
    pyramidView.lastTickMs = performance.now() - c.tickT0;
  } },

  ];

  function tick(): void {
    if (deps.live.disposed) return;
    // The sim's own tick drives the world in cruise (`atlasWorld.update`), and
    // the focus rig, the ladder and the draw all belong to the frame where the
    // Atlas owns the camera. Nothing here runs while it does not.
    if (deps.worldHostState.externalWorld) return;
    ctx.tickT0 = performance.now();
    // A menu closed over the Atlas (or any ancestor gone display:none) leaves
    // root connected at 0x0 rather than unmounting it, so this cannot rely on
    // disposal: skip the draw and the governor sample, and leave `needsRender`
    // set so the first visible frame redraws.
    ctx.hidden = root.clientWidth === 0;
    runSystems(systems, ctx, NO_PROFILER);
  }

  return { tick, systems };
}
